import ChannelsAppsSidebarList from "@/chat/sidebar/channels/ChannelsAppsSidebarList"
import ChannelsNavSection from "@/chat/sidebar/channels/ChannelsNavSection"
import ChannelsProductsSidebarList from "@/chat/sidebar/channels/ChannelsProductsSidebarList"
import { ChannelsSidebarQuery } from "@/chat/sidebar/channels/__generated__/ChannelsSidebarQuery.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  DiscoDivider,
  DiscoSideBarItemSkeleton,
  DiscoText,
  DiscoTextSkeleton,
} from "@disco-ui"
import { range } from "@utils/array/arrayUtils"
import { Fragment } from "react"
import { useLazyLoadQuery } from "react-relay"
import { Redirect } from "react-router-dom"
import { graphql } from "relay-runtime"

function ChannelsSidebar() {
  const activeOrganization = useActiveOrganization()!
  const classes = useStyles()

  const data = useLazyLoadQuery<ChannelsSidebarQuery>(
    graphql`
      query ChannelsSidebarQuery {
        isViewerInAnyChannels
        # Top level community channels
        ...ChannelsAppsSidebarList_OrganizationPaginationFragment

        # Community channels in nav sections
        channelsSidebarCommunityNavSections {
          edges {
            node {
              id
              kind
              ...ChannelsNavSectionFragment
            }
          }
        }

        # Top level product chat channels
        ...ChannelsProductsSidebarList_OrganizationPaginationFragment

        # Product channels in nav sections
        channelsSidebarProductNavSections {
          edges {
            node {
              id
              kind
              ...ChannelsNavSectionFragment
            }
          }
        }
      }
    `,
    {},
    { fetchPolicy: "network-only", fetchKey: "channels-side-bar" }
  )

  const {
    isViewerInAnyChannels,
    channelsSidebarCommunityNavSections,
    channelsSidebarProductNavSections,
  } = data

  if (!activeOrganization.isChannelsEnabled) {
    return <Redirect to={ROUTE_NAMES.COMMUNITY.HOME.ROOT} />
  }

  if (!isViewerInAnyChannels) {
    return (
      <div className={classes.inner}>
        <DiscoText
          variant={"body-sm"}
          color={"groovy.neutral.400"}
          marginTop={2}
          align={"center"}
        >
          {"No channels yet."}
        </DiscoText>
      </div>
    )
  }

  const communitySections = Relay.connectionToArray(channelsSidebarCommunityNavSections)
  const productSections = Relay.connectionToArray(channelsSidebarProductNavSections)

  return (
    <div className={classes.outer}>
      {/* Top level community channels */}
      <ChannelsAppsSidebarList topLevelKey={data} />

      {/* Community channels in nav sections */}
      {communitySections.map((section, i) => (
        <Fragment key={section.id}>
          <DiscoDivider marginTop={1.5} marginBottom={1.5} />

          <ChannelsNavSection
            testid={`CommunityNavSection.${i}`}
            navSectionKey={section}
          />
        </Fragment>
      ))}

      {/* Top level product chat channels */}
      <ChannelsProductsSidebarList testid={"TopLevelProducts"} topLevelKey={data} />

      {/* Product channels in nav sections */}
      {productSections.map((section, i) => (
        <Fragment key={section.id}>
          <DiscoDivider marginTop={1.5} marginBottom={1.5} />

          <ChannelsNavSection
            testid={`ProductsNavSection.${i}`}
            navSectionKey={section}
          />
        </Fragment>
      ))}
    </div>
  )
}

const useStyles = makeUseStyles((theme) => ({
  outer: {
    padding: theme.spacing(0, 0, 2),
  },
  inner: {
    padding: theme.spacing(0, 2),
  },
}))

function ChannelsSidebarSkeleton() {
  const classes = useStyles()
  return (
    <div
      className={classes.inner}
      style={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      {range(4).map((i) => (
        <DiscoSideBarItemSkeleton key={i} />
      ))}
      {range(2).map((i) => (
        <Fragment key={i}>
          <DiscoDivider />
          <DiscoTextSkeleton width={"100px"} />
          <DiscoSideBarItemSkeleton />
          <DiscoSideBarItemSkeleton />
        </Fragment>
      ))}
    </div>
  )
}

export default Relay.withSkeleton({
  component: ChannelsSidebar,
  skeleton: ChannelsSidebarSkeleton,
})
